/**
 * TopBarUserActions - Bloc des 3 actions utilisateur (etablissement / user / deconnexion)
 *
 * Ce composant regroupe la logique et l'UI des 3 elements situes a droite de la TopBar,
 * et fournit deux variantes d'affichage :
 *
 *   - variant='topbar' : rendu horizontal classique (badges + popovers + bouton),
 *     identique au TopBar actuel sur desktop. Aucune regression visuelle.
 *
 *   - variant='drawer' : rendu vertical empile, destine au pied de drawer mobile.
 *     Pas de popovers absolus : la liste des etablissements s'expand inline.
 *
 * Toute la logique (loadContext, handleSwitch, ChangePasswordModal) est centralisee ici.
 */

import { useState, useEffect, useRef } from 'react';
import { SavUser, ROLE_LABELS, usePermissionScope, supabase } from '../../SupabaseWrapper';
import { scopeIsAtLeast } from '../../shared/permissions';
import { LogoutIcon, BuildingIcon, UserIcon } from '../../icons';
import ChangePasswordModal from '../ChangePasswordModal';
import SecuriteCompteModal from '../SecuriteCompteModal';
import { useViewport } from '../../hooks/useViewport';

interface Establishment {
  id: string;
  name: string;
  code: string;
  dossier: string;
  isCurrent: boolean;
  organization_id?: string;
  organization_name?: string;
  account_id?: string;
  account_name?: string;
}

interface AuthContext {
  account: { id: string; name: string | null };
  organization: { id: string; name: string | null; code?: string; mode?: string; deployment_type?: string };
  establishment: { id: string; name: string | null; code?: string; dossier?: string };
  canSwitch?: boolean;
  availableEstablishments: Establishment[];
}

// ---------------------------------------------------------------------------
// Regroupement hierarchique du switcher : Compte > Organisation > Etablissement
// ---------------------------------------------------------------------------
interface OrgGroup { key: string; name: string; estabs: Establishment[]; }
interface AccountGroup { key: string; name: string; orgs: OrgGroup[]; }

/**
 * Regroupe une liste plate d'etablissements en Compte > Organisation > Etablissement,
 * en preservant l'ordre d'arrivee (deja trie par nom cote backend).
 * - `isMultiAccount` : plusieurs comptes presents (cas super_admin cross-account).
 * - `hasOrgInfo` : au moins un etablissement porte un nom d'organisation.
 */
function groupByAccountOrg(estabs: Establishment[]): {
  accounts: AccountGroup[];
  isMultiAccount: boolean;
  hasOrgInfo: boolean;
} {
  const hasOrgInfo = estabs.some(e => e.organization_name);
  const accMap = new Map<string, AccountGroup>();
  for (const e of estabs) {
    const accKey = e.account_id || e.account_name || '_single_';
    const orgKey = hasOrgInfo ? (e.organization_id || e.organization_name || '_org_') : '_flat_';
    let acc = accMap.get(accKey);
    if (!acc) { acc = { key: accKey, name: e.account_name || '', orgs: [] }; accMap.set(accKey, acc); }
    let org = acc.orgs.find(o => o.key === orgKey);
    if (!org) { org = { key: orgKey, name: e.organization_name || '', estabs: [] }; acc.orgs.push(org); }
    org.estabs.push(e);
  }
  const accounts = [...accMap.values()];
  return { accounts, isMultiAccount: accounts.length > 1, hasOrgInfo };
}

interface TopBarUserActionsProps {
  user: SavUser | null;
  onLogout: () => void;
  variant: 'topbar' | 'drawer';
  /** Appele quand une action navigue / change l'etat (ex: drawer doit se fermer) */
  onAction?: () => void;
}

function TopBarUserActions({ user, onLogout, variant, onAction }: TopBarUserActionsProps) {
  // ----- State partage -----
  const [context, setContext] = useState<AuthContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSecurite, setShowSecurite] = useState(false);

  // ----- State variant 'topbar' -----
  const [showPopover, setShowPopover] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userBadgeRef = useRef<HTMLDivElement>(null);

  // ----- State variant 'drawer' -----
  const [drawerEstabExpanded, setDrawerEstabExpanded] = useState(false);

  // Le switcher est visible si l'utilisateur a interventions.view au moins au scope organisation
  const interventionsScope = usePermissionScope('interventions.view');
  const canSwitch = interventionsScope ? scopeIsAtLeast(interventionsScope, 'organization') : false;
  const { isPhone } = useViewport();

  // Fermer le popover etablissement au clic en dehors (variant topbar)
  useEffect(() => {
    if (variant !== 'topbar' || !showPopover) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        badgeRef.current && !badgeRef.current.contains(e.target as Node)
      ) {
        setShowPopover(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover, variant]);

  // Fermer le menu utilisateur au clic en dehors (variant topbar)
  useEffect(() => {
    if (variant !== 'topbar' || !showUserMenu) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        userMenuRef.current && !userMenuRef.current.contains(e.target as Node) &&
        userBadgeRef.current && !userBadgeRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, variant]);

  // Charger le contexte (cache : ne charge qu'une fois)
  async function loadContext() {
    if (context) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('sso_token');
      const res = await fetch('/api/auth/context', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setContext(data);
      }
    } catch (err) {
      console.error('[TopBarUserActions] Erreur chargement contexte:', err);
    } finally {
      setLoading(false);
    }
  }

  // Charger le contexte au montage (et plus seulement au clic) pour disposer
  // du libellé etablissement sans dependre de user.dossier.
  useEffect(() => {
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSwitch(estab: Establishment) {
    if (estab.isCurrent || switching) return;
    setSwitching(estab.id);
    try {
      const token = localStorage.getItem('sso_token');
      const res = await fetch('/api/auth/switch-establishment', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ establishment_id: estab.id })
      });
      const data = await res.json();
      if (data.success) {
        // Le backend a mis a jour raw_app_meta_data via auth.admin.updateUserById,
        // mais le JWT actuel en localStorage est inchange : un simple reload
        // relit le meme token. Il faut forcer un refresh de session pour qu'un
        // nouveau JWT soit emis (le custom_access_token_hook recalculera alors
        // role/permissions/establishment_id).
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('[TopBarUserActions] Erreur refresh session:', refreshError);
          setSwitching(null);
          return;
        }
        window.location.reload();
      } else {
        console.error('[TopBarUserActions] Erreur switch:', data.error);
        setSwitching(null);
      }
    } catch (err) {
      console.error('[TopBarUserActions] Erreur switch:', err);
      setSwitching(null);
    }
  }

  function handleBadgeClick() {
    if (!canSwitch) return;
    setShowPopover(!showPopover);
    if (!showPopover) loadContext();
  }

  function handleDrawerEstabToggle() {
    if (!canSwitch) return;
    const next = !drawerEstabExpanded;
    setDrawerEstabExpanded(next);
    if (next) loadContext();
  }

  // Label du badge : derive de context (resolu depuis establishment_id),
  // jamais de user.dossier. Repli neutre tant que context n'est pas charge.
  const estabLabel = context?.establishment?.name || context?.organization?.name || 'Établissement';
  const badgeLabel = context?.organization?.name
    ? `${context.organization.name} · ${context?.establishment?.dossier || context?.establishment?.code || estabLabel}`
    : estabLabel;

  // ============================================================================
  // VARIANT 'drawer' — empilement vertical en pied de drawer mobile
  // ============================================================================
  if (variant === 'drawer') {
    // Palette sombre coherente avec la Sidebar (#1e293b)
    const BG = '#1e293b';
    const BORDER = 'rgba(255,255,255,0.08)';
    const TEXT_PRIMARY = '#f1f5f9';
    const TEXT_MUTED = '#94a3b8';
    const TEXT_DIM = '#64748b';
    const SURFACE = 'rgba(255,255,255,0.05)';
    const SURFACE_HOVER = 'rgba(255,255,255,0.08)';
    const ACCENT_BG = 'rgba(59,130,246,0.18)';
    const ACCENT_TEXT = '#93c5fd';

    return (
      <div style={{
        backgroundColor: BG,
        color: TEXT_PRIMARY,
        borderTop: `1px solid ${BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        maxHeight: '65vh',
        overflowY: 'auto'
      }}>
        {/* Section utilisateur */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              backgroundColor: '#334155',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <UserIcon size={18} color="#93c5fd" />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {user?.display_name || user?.username || 'Utilisateur'}
              </div>
              {user?.email && (
                <div style={{
                  fontSize: 12, color: TEXT_MUTED,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {user.email}
                </div>
              )}
              {user?.role && (
                <div style={{
                  display: 'inline-block',
                  marginTop: 4,
                  fontSize: 10, fontWeight: 600,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: ACCENT_TEXT,
                  backgroundColor: ACCENT_BG,
                  padding: '2px 8px',
                  borderRadius: 4
                }}>
                  {ROLE_LABELS[user.role] || user.role}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setShowChangePassword(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', marginTop: 12,
              padding: '9px 12px',
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              textAlign: 'left',
              fontSize: 13,
              color: TEXT_PRIMARY,
              cursor: 'pointer',
              transition: 'background-color 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = SURFACE_HOVER}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = SURFACE}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Changer mon mot de passe
          </button>
          <button
            type="button"
            data-ouvrir-securite
            onClick={() => { setShowSecurite(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', marginTop: 8,
              padding: '9px 12px',
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              textAlign: 'left',
              fontSize: 13,
              color: TEXT_PRIMARY,
              cursor: 'pointer',
              transition: 'background-color 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = SURFACE_HOVER}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = SURFACE}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Sécurité du compte
          </button>
        </div>

        {/* Section etablissement */}
        {(canSwitch || user?.establishment_id) && (
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDER}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: canSwitch ? 8 : 0 }}>
              <BuildingIcon size={16} color={TEXT_MUTED} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: TEXT_DIM
                }}>
                  Établissement
                </div>
                <div style={{
                  fontSize: 14, color: TEXT_PRIMARY, fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {badgeLabel || '—'}
                </div>
              </div>
            </div>

            {canSwitch && (
              <button
                type="button"
                onClick={handleDrawerEstabToggle}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: drawerEstabExpanded ? ACCENT_BG : SURFACE,
                  border: `1px solid ${drawerEstabExpanded ? 'rgba(59,130,246,0.4)' : BORDER}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: drawerEstabExpanded ? ACCENT_TEXT : TEXT_PRIMARY,
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => { if (!drawerEstabExpanded) e.currentTarget.style.backgroundColor = SURFACE_HOVER; }}
                onMouseLeave={(e) => { if (!drawerEstabExpanded) e.currentTarget.style.backgroundColor = SURFACE; }}
              >
                <span>Changer d'établissement</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{
                    transform: drawerEstabExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}

            {canSwitch && drawerEstabExpanded && (
              <div style={{
                marginTop: 8,
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                overflow: 'hidden'
              }}>
                {loading ? (
                  <div style={{ padding: 20, textAlign: 'center' }}>
                    <div style={{
                      width: 20, height: 20,
                      border: '2px solid rgba(255,255,255,0.1)', borderTop: '2px solid #93c5fd',
                      borderRadius: '50%', margin: '0 auto',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  </div>
                ) : context ? (
                  <div style={{ padding: 6 }}>
                    {(() => {
                      const { accounts, isMultiAccount, hasOrgInfo } = groupByAccountOrg(context.availableEstablishments);

                      const renderRow = (estab: Establishment) => (
                        <div
                          key={estab.id}
                          onClick={() => handleSwitch(estab)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderRadius: 6,
                            cursor: estab.isCurrent ? 'default' : 'pointer',
                            backgroundColor: estab.isCurrent ? ACCENT_BG : 'transparent',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => { if (!estab.isCurrent) e.currentTarget.style.backgroundColor = SURFACE; }}
                          onMouseLeave={(e) => { if (!estab.isCurrent) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: '50%',
                            border: estab.isCurrent ? '2px solid #93c5fd' : '2px solid rgba(255,255,255,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {estab.isCurrent && (
                              <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#93c5fd' }} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 13,
                              fontWeight: estab.isCurrent ? 600 : 400,
                              color: estab.isCurrent ? ACCENT_TEXT : TEXT_PRIMARY,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {estab.name}
                            </div>
                            {estab.dossier && (
                              <div style={{ fontSize: 11, color: TEXT_MUTED }}>{estab.dossier}</div>
                            )}
                          </div>
                          {switching === estab.id && (
                            <div style={{
                              width: 14, height: 14,
                              border: '2px solid rgba(255,255,255,0.1)', borderTop: '2px solid #93c5fd',
                              borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                            }} />
                          )}
                        </div>
                      );

                      return accounts.map((acc, ai) => {
                        const showOrgHeaders = hasOrgInfo && (isMultiAccount || acc.orgs.length > 1);
                        return (
                          <div key={acc.key}>
                            {isMultiAccount && (
                              <div style={{
                                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '0.5px', color: '#c7d2fe',
                                padding: ai === 0 ? '6px 8px 4px' : '14px 8px 4px',
                                borderTop: ai === 0 ? 'none' : `1px solid ${BORDER}`
                              }}>
                                {acc.name || 'Compte'}
                              </div>
                            )}
                            {acc.orgs.map((org, oi) => (
                              <div key={org.key} style={isMultiAccount ? { paddingLeft: 6 } : undefined}>
                                {showOrgHeaders && (
                                  <div style={{
                                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                    letterSpacing: '0.5px', color: '#a5b4fc',
                                    padding: (!isMultiAccount && ai === 0 && oi === 0) ? '6px 8px' : '10px 8px 6px',
                                    borderTop: (!isMultiAccount && oi > 0) ? `1px solid ${BORDER}` : 'none'
                                  }}>
                                    {org.name || 'Organisation'}
                                  </div>
                                )}
                                {org.estabs.map(renderRow)}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div style={{ padding: 16, color: TEXT_MUTED, fontSize: 13, textAlign: 'center' }}>
                    Erreur de chargement
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Section deconnexion */}
        <div style={{ padding: '14px 16px' }}>
          <button
            onClick={() => { onLogout(); if (onAction) onAction(); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%',
              padding: '11px',
              backgroundColor: 'rgba(239,68,68,0.12)',
              color: '#fca5a5',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.2)';
              e.currentTarget.style.color = '#fecaca';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.12)';
              e.currentTarget.style.color = '#fca5a5';
            }}
          >
            <LogoutIcon size={16} />
            Déconnexion
          </button>
        </div>

        {showChangePassword && user?.email && (
          <ChangePasswordModal
            email={user.email}
            onClose={() => { setShowChangePassword(false); if (onAction) onAction(); }}
            onSuccess={() => { setShowChangePassword(false); if (onAction) onAction(); }}
          />
        )}
        {showSecurite && (
          <SecuriteCompteModal
            role={user?.role}
            onClose={() => { setShowSecurite(false); if (onAction) onAction(); }}
          />
        )}
      </div>
    );
  }

  // ============================================================================
  // VARIANT 'topbar' — rendu identique au TopBar actuel (desktop unchanged)
  // ============================================================================
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isPhone ? 8 : 20 }}>
      {/* Badge etablissement (cliquable si admin) */}
      {(canSwitch || user?.establishment_id) && (
        <div style={{ position: 'relative' }}>
          <div
            ref={badgeRef}
            onClick={handleBadgeClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: showPopover ? '#e0e7ff' : '#f1f5f9',
              borderRadius: '8px',
              cursor: canSwitch ? 'pointer' : 'default',
              transition: 'background-color 0.15s',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              if (canSwitch && !showPopover) e.currentTarget.style.backgroundColor = '#e2e8f0';
            }}
            onMouseLeave={(e) => {
              if (canSwitch && !showPopover) e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
          >
            <BuildingIcon size={16} color={showPopover ? '#4f46e5' : '#64748b'} />
            {!isPhone && (
              <span style={{
                fontSize: '13px',
                color: showPopover ? '#4f46e5' : '#475569',
                fontWeight: '500'
              }}>
                {badgeLabel}
              </span>
            )}
            {canSwitch && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke={showPopover ? '#4f46e5' : '#94a3b8'} strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
                style={{
                  transform: showPopover ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>

          {/* Popover */}
          {showPopover && (
            <div
              ref={popoverRef}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: isPhone ? 'min(280px, calc(100vw - 24px))' : '280px',
                maxWidth: 'calc(100vw - 24px)',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)',
                border: '1px solid #e2e8f0',
                zIndex: 1300,
                overflow: 'hidden'
              }}
            >
              {loading ? (
                <div style={{ padding: '24px', textAlign: 'center' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #e2e8f0',
                    borderTop: '2px solid #3b82f6',
                    borderRadius: '50%',
                    margin: '0 auto',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
              ) : context ? (
                <>
                  {/* Header : Compte + Organisation */}
                  <div style={{ padding: '16px 16px 12px' }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: '#94a3b8',
                      marginBottom: '4px'
                    }}>
                      Compte
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#334155',
                      fontWeight: '500'
                    }}>
                      {context.account?.name || '—'}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: '#94a3b8',
                      marginTop: '12px',
                      marginBottom: '4px'
                    }}>
                      Organisation
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#334155',
                      fontWeight: '500'
                    }}>
                      {context.organization?.name || '—'}
                    </div>
                  </div>

                  {/* Separateur */}
                  <div style={{ height: '1px', backgroundColor: '#e2e8f0' }} />

                  {/* Liste des etablissements */}
                  <div style={{ padding: '8px' }}>
                    {(() => {
                      const { accounts, isMultiAccount, hasOrgInfo } = groupByAccountOrg(context.availableEstablishments);

                      const renderRow = (estab: Establishment) => (
                        <div
                          key={estab.id}
                          onClick={() => handleSwitch(estab)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            cursor: estab.isCurrent ? 'default' : 'pointer',
                            backgroundColor: estab.isCurrent ? '#eff6ff' : 'transparent',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            if (!estab.isCurrent) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (!estab.isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{
                            width: '18px', height: '18px', borderRadius: '50%',
                            border: estab.isCurrent ? '2px solid #3b82f6' : '2px solid #cbd5e1',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            {estab.isCurrent && (
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: estab.isCurrent ? '600' : '400',
                              color: estab.isCurrent ? '#1e40af' : '#334155'
                            }}>
                              {estab.name}
                            </div>
                            {estab.dossier && (
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{estab.dossier}</div>
                            )}
                          </div>
                          {switching === estab.id && (
                            <div style={{
                              width: '16px', height: '16px',
                              border: '2px solid #e2e8f0', borderTop: '2px solid #3b82f6',
                              borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                            }} />
                          )}
                        </div>
                      );

                      return accounts.map((acc, ai) => {
                        const showOrgHeaders = hasOrgInfo && (isMultiAccount || acc.orgs.length > 1);
                        return (
                          <div key={acc.key}>
                            {isMultiAccount && (
                              <div style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#1e293b',
                                padding: ai === 0 ? '4px 8px 6px' : '14px 8px 6px',
                                borderTop: ai === 0 ? 'none' : '1px solid #e2e8f0'
                              }}>
                                {acc.name || 'Compte'}
                              </div>
                            )}
                            {acc.orgs.map((org, oi) => (
                              <div key={org.key} style={isMultiAccount ? { paddingLeft: '6px' } : undefined}>
                                {showOrgHeaders ? (
                                  <div style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    color: '#6366f1',
                                    padding: (isMultiAccount || oi > 0) ? '10px 8px 6px' : '4px 8px 6px',
                                    borderTop: (!isMultiAccount && oi > 0) ? '1px solid #e2e8f0' : 'none'
                                  }}>
                                    {org.name || 'Organisation'}
                                  </div>
                                ) : (!isMultiAccount && (
                                  <div style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    color: '#94a3b8',
                                    padding: '4px 8px 8px'
                                  }}>
                                    Établissements
                                  </div>
                                ))}
                                {org.estabs.map(renderRow)}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>
              ) : (
                <div style={{ padding: '16px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>
                  Erreur de chargement
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Info utilisateur */}
      <div style={{ position: 'relative' }}>
        <div
          ref={userBadgeRef}
          data-ouvrir-menu-utilisateur
          onClick={() => setShowUserMenu(!showUserMenu)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '4px 8px',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: showUserMenu ? '#e0e7ff' : 'transparent',
            transition: 'background-color 0.15s',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => { if (!showUserMenu) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          onMouseLeave={(e) => { if (!showUserMenu) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div style={{
            width: '36px',
            height: '36px',
            backgroundColor: '#dbeafe',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserIcon size={18} color="#3b82f6" />
          </div>
          {!isPhone && (
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#1e293b'
              }}>
                {user?.display_name || user?.username || 'Utilisateur'}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#64748b'
              }}>
                {user?.role ? ROLE_LABELS[user.role] || user.role : 'Utilisateur'}
              </div>
            </div>
          )}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke={showUserMenu ? '#4f46e5' : '#94a3b8'} strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
            style={{
              transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {showUserMenu && (
          <div
            ref={userMenuRef}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              minWidth: isPhone ? 'min(240px, calc(100vw - 24px))' : '240px',
              maxWidth: 'calc(100vw - 24px)',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0',
              zIndex: 1300,
              overflow: 'hidden',
              padding: '6px'
            }}
          >
            {user?.email && (
              <div style={{
                padding: '8px 12px 12px',
                borderBottom: '1px solid #e2e8f0',
                marginBottom: '6px'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}>
                  Connecté en tant que
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#334155',
                  fontWeight: '500',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {user.email}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                color: '#334155',
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Changer mon mot de passe
            </button>
            <button
              type="button"
              data-ouvrir-securite
              onClick={() => { setShowUserMenu(false); setShowSecurite(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                color: '#334155',
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Sécurité du compte
            </button>
          </div>
        )}
      </div>

      {/* Bouton deconnexion */}
      <button
        onClick={onLogout}
        aria-label="Déconnexion"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: isPhone ? '10px' : '8px 16px',
          backgroundColor: 'transparent',
          color: '#64748b',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.15s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#fef2f2';
          e.currentTarget.style.borderColor = '#fecaca';
          e.currentTarget.style.color = '#dc2626';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.color = '#64748b';
        }}
      >
        <LogoutIcon size={16} />
        {!isPhone && 'Déconnexion'}
      </button>

      {showChangePassword && user?.email && (
        <ChangePasswordModal
          email={user.email}
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => setShowChangePassword(false)}
        />
      )}

      {showSecurite && (
        <SecuriteCompteModal role={user?.role} onClose={() => setShowSecurite(false)} />
      )}
    </div>
  );
}

export default TopBarUserActions;
